import { useState } from 'react';
import { Wallet, CheckCircle2, Clock, TrendingUp, Calendar, CreditCard, RefreshCw, AlertCircle } from 'lucide-react';
import { useAffiliateProfile, useCommissions, useRequestPayout, useInitiatePayout } from '../hooks/useAffiliate';

export default function Payouts() {
  const { data: profile, isLoading: profileLoading } = useAffiliateProfile();
  const { data: commissionsResponse, isLoading: commissionsLoading } = useCommissions({ status: 'APPROVED' });
  const { mutate: initiatePayout, isPending: isInitiating } = useInitiatePayout();
  const { mutate: finalizePayout, isPending: isFinalizing } = useRequestPayout();
  
  const [requestError, setRequestError] = useState<string | null>(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [mpesaMethod, setMpesaMethod] = useState<'B2C' | 'B2B' | 'B2POCHI'>('B2C');
  const [destination, setDestination] = useState('');

  const approvedCommissions = commissionsResponse?.items || [];
  const availableForPayout = approvedCommissions.reduce((sum, c) => sum + c.commissionAmount, 0);

  const totalPaidOut = profile?.totalEarnings || 0;

  const isLoading = profileLoading || commissionsLoading;

  const handleStartPayout = () => {
    if (!destination) {
      setRequestError('Please enter a destination number');
      return;
    }
    setRequestError(null);
    initiatePayout({
      amount: availableForPayout,
      destination,
      mpesaMethod
    }, {
      onSuccess: (data) => {
        setVerificationToken(data.token);
        setShowVerification(true);
      },
      onError: (error: any) => {
        setRequestError(error?.response?.data?.error || 'Failed to initiate payout');
      }
    });
  };

  const handleFinalizePayout = () => {
    setRequestError(null);
    finalizePayout({ 
      token: verificationToken, 
      code: verificationCode,
      destination,
      mpesaMethod
    }, {
      onSuccess: () => {
        setShowVerification(false);
        alert('Payout request submitted successfully! Our team will process it shortly.');
        setVerificationCode('');
        setVerificationToken('');
      },
      onError: (error: any) => {
        setRequestError(error?.response?.data?.error || 'Verification failed');
      }
    });
  };


  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 pb-6">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Payouts</h1>
        <p className="text-zinc-600 dark:text-zinc-400 text-lg">
          Manage your earnings and payment information
        </p>
      </div>

      {/* Stats Grid */}
      {isLoading ? (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-12">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-zinc-300 dark:border-zinc-700 border-t-zinc-900 dark:border-t-zinc-100 rounded-full animate-spin mb-4"></div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium">Loading payout information...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Available for Payout */}
            <div className="bg-emerald-600 rounded-lg p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center">
                    <Wallet className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-xs font-bold text-white bg-white/20 px-3 py-1.5 rounded-lg">AVAILABLE</span>
                </div>
                <p className="text-5xl font-black text-white mb-2">
                  KES {availableForPayout.toLocaleString()}
                </p>
                <p className="text-emerald-100 font-medium">Ready for withdrawal</p>
              </div>
            </div>

            {/* Total Paid Out */}
            <div className="bg-zinc-900 dark:bg-zinc-100 rounded-lg p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 dark:bg-zinc-900/5 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 dark:bg-zinc-900/5 rounded-full -ml-12 -mb-12"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-white/10 dark:bg-zinc-900/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-white dark:text-zinc-900" />
                  </div>
                  <span className="text-xs font-bold text-white dark:text-zinc-900 bg-white/20 dark:bg-zinc-900/20 px-3 py-1.5 rounded-lg">LIFETIME</span>
                </div>
                <p className="text-5xl font-black text-white dark:text-zinc-900 mb-2">
                  KES {totalPaidOut.toLocaleString()}
                </p>
                <p className="text-zinc-400 dark:text-zinc-600 font-medium">Total earnings to date</p>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-950 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="text-xl font-black text-zinc-900 dark:text-zinc-100">Payment Method</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-zinc-700 dark:text-zinc-300 mb-2">Disbursement Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'B2C', label: 'Phone' },
                    { id: 'B2POCHI', label: 'Pochi' },
                    { id: 'B2B', label: 'Till/Paybill' }
                  ].map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => {
                        setMpesaMethod(method.id as any);
                        if (method.id === 'B2C' && profile?.paymentPhone && !destination) {
                          setDestination(profile.paymentPhone);
                        }
                      }}
                      className={`px-3 py-2 text-sm font-bold rounded-lg border transition-all ${
                        mpesaMethod === method.id 
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' 
                          : 'bg-zinc-50 dark:bg-zinc-950 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-800 hover:border-emerald-500'
                      }`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>

              {mpesaMethod === 'B2B' && (
                <div className="bg-emerald-50 dark:bg-emerald-950/20 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 flex gap-4">
                  <label className="flex items-center gap-2 text-sm font-bold text-emerald-800 dark:text-emerald-400 cursor-pointer">
                    <input 
                      type="radio" 
                      name="b2bType" 
                      checked={!destination.startsWith('PAY-')} 
                      onChange={() => setDestination(prev => prev.replace('PAY-', ''))}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    Till Number
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-emerald-800 dark:text-emerald-400 cursor-pointer">
                    <input 
                      type="radio" 
                      name="b2bType" 
                      checked={destination.startsWith('PAY-')} 
                      onChange={() => setDestination(prev => prev.startsWith('PAY-') ? prev : `PAY-${prev}`)}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    Paybill Number
                  </label>
                </div>
              )}

              {mpesaMethod === 'B2POCHI' && (
                <div className="bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-400">
                    Ensure the recipient has <strong>activated Pochi la Biashara</strong> on their M-Pesa line before requesting.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-zinc-700 dark:text-zinc-300 mb-2">
                  {mpesaMethod === 'B2C' ? 'Phone Number' : 
                   mpesaMethod === 'B2POCHI' ? 'Pochi Phone Number' : 
                   destination.startsWith('PAY-') ? 'Paybill Number' : 'Till Number'}
                </label>
                <div className="bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-4">
                    <input
                      type="text"
                      value={destination.replace('PAY-', '')}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setDestination(destination.startsWith('PAY-') ? `PAY-${val}` : val);
                      }}
                      placeholder={mpesaMethod === 'B2C' ? '2547...' : mpesaMethod === 'B2POCHI' ? '2547...' : '123456'}
                      className="flex-1 bg-transparent border-none p-0 text-lg font-bold text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:ring-0 outline-none"
                    />
                    {mpesaMethod === 'B2C' && profile?.paymentPhone && destination !== profile.paymentPhone && (
                      <button 
                        type="button"
                        onClick={() => setDestination(profile.paymentPhone!)}
                        className="text-xs font-bold text-emerald-600 hover:underline"
                      >
                        Use Profile
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showVerification && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-8 w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-300">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-950 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-black text-zinc-900 dark:text-zinc-100 mb-2">Verify Withdrawal</h2>
                  <p className="text-zinc-600 dark:text-zinc-400">
                    We've sent a 6-digit verification code to your email. Please enter it below to authorize this payout.
                  </p>
                </div>
                
                <div className="space-y-6">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                    className="w-full text-center text-4xl font-black tracking-[0.5em] py-4 bg-zinc-50 dark:bg-zinc-950 border-2 border-zinc-200 dark:border-zinc-800 rounded-xl focus:border-emerald-500 focus:ring-0 transition-all outline-none"
                    placeholder="000000"
                    autoFocus
                  />
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowVerification(false);
                        setVerificationCode('');
                      }}
                      className="flex-1 px-6 py-3 border border-zinc-200 dark:border-zinc-800 rounded-xl font-bold text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-950 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFinalizePayout}
                      disabled={isFinalizing || verificationCode.length !== 6}
                      className="flex-[2] px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-400 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                    >
                      {isFinalizing ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5" />
                      )}
                      {isFinalizing ? 'Verifying...' : 'Finalize Payout'}
                    </button>
                  </div>

                  <p className="text-center text-xs text-zinc-500">
                    Didn't receive the code? <button onClick={handleStartPayout} className="text-emerald-600 font-bold hover:underline">Resend</button>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Approved Commissions Awaiting Payout */}
          {requestError && (
            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{requestError}</p>
            </div>
          )}

          {approvedCommissions.length > 0 ? (
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-black text-zinc-900 dark:text-zinc-100">Approved Commissions</h2>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">These commissions are ready for payout</p>
                </div>
                <button
                  onClick={handleStartPayout}
                  disabled={isInitiating || isFinalizing || !destination || availableForPayout <= 0}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-400 text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center gap-2 shadow-sm whitespace-nowrap"
                >
                  {isInitiating ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Wallet className="w-4 h-4" />
                  )}
                  {isInitiating ? 'Sending Code...' : 'Request Payout'}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50 dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Order</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Approved Date</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                    {approvedCommissions.map((commission) => (
                      <tr key={commission.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-950 transition-colors">
                        <td className="px-6 py-4">
                          <div className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                            {commission.link.product.title}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-500 font-mono">
                            {commission.link.code}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                            #{commission.order.orderNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                            <Calendar className="w-4 h-4 text-zinc-500" />
                            {commission.approvedAt
                              ? new Date(commission.approvedAt).toLocaleDateString()
                              : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-black text-emerald-600">
                            KES {commission.commissionAmount.toLocaleString()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-zinc-50 dark:bg-zinc-950 border-t-2 border-zinc-300 dark:border-zinc-700">
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-right">
                        <span className="text-sm font-bold text-zinc-900 dark:text-zinc-100 uppercase tracking-wider">Total Available:</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xl font-black text-emerald-600">
                          KES {availableForPayout.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-12">
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Wallet className="w-10 h-10 text-zinc-400 dark:text-zinc-600" />
                </div>
                <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">No approved commissions</h3>
                <p className="text-zinc-600 dark:text-zinc-400">
                  Approved commissions ready for payout will appear here
                </p>
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
}
